<template>
  <v-container fluid>
    <v-card outlined :loading="loading" v-if="!loadingSettings">
      <validation-observer ref="form">
        <v-form @submit.prevent="onSubmit" :disabled="loading">
          <v-card-text>
            <v-tabs v-model="activeTab" grow>
              <v-tab :class="{ 'error-tab': tabErrors[0] }">
                {{ $t('findInStore.settings.general') }}
                <v-icon v-if="tabErrors[0]" color="error" small class="mr-1">mdi-alert-circle</v-icon>
              </v-tab>
              <v-tab :class="{ 'error-tab': tabErrors[1] }">
                {{ $t('findInStore.settings.catalog') }}
                <v-icon v-if="tabErrors[1]" color="error" small class="mr-1">mdi-alert-circle</v-icon>
              </v-tab>
              <v-tab :class="{ 'error-tab': tabErrors[2] }">
                {{ $t('findInStore.settings.customization.label') }}
                <v-icon v-if="tabErrors[2]" color="error" small class="mr-1">mdi-alert-circle</v-icon>
              </v-tab>
              <v-tab :class="{ 'error-tab': tabErrors[3] }">
                {{ $t('findInStore.settings.stock') }}
                <v-icon v-if="tabErrors[3]" color="error" small class="mr-1">mdi-alert-circle</v-icon>
              </v-tab>
              <v-tab :class="{ 'error-tab': tabErrors[4] }">
                {{ $t('drawer.configurations.tunnel') }}
                <v-icon v-if="tabErrors[4]" color="error" small class="mr-1">mdi-alert-circle</v-icon>
              </v-tab>
              <v-tab-item eager>
                <FindInStoreSettingsGeneral ref="generalTab" :settings.sync="settingsForm.general" />
              </v-tab-item>
              <v-tab-item eager>
                <FindInStoreSettingsCatalog
                  ref="catalogTab"
                  :settings.sync="settingsForm.catalog"
                  :catalog-activated="settingsForm.catalog.enabled"
                  @update:catalogActivated="updateCatalogActivated"
                />
              </v-tab-item>
              <v-tab-item eager>
                <FindInStoreSettingsCustomization ref="customizationTab" :settings.sync="settingsForm.customizations" />
              </v-tab-item>
              <v-tab-item eager>
                <FindInStoreSettingsStock ref="stockTab" :settings.sync="settingsForm.stock" />
              </v-tab-item>
              <v-tab-item eager>
                <FindInStoreSettingsCheckout
                  ref="stockCheckout"
                  :settings.sync="settingsForm.checkout"
                  :checkout-activated="settingsForm.checkout.enabled"
                  @update:checkoutActivated="updateCheckoutActivated"
                />
              </v-tab-item>
            </v-tabs>
          </v-card-text>
          <v-card-actions class="justify-center">
            <v-btn class="ma-2" color="primary" outlined type="submit" :loading="loading">
              {{ $t('findInStore.settings.save') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </v-card>
  </v-container>
</template>

<script>
  import FindInStoreSettingsGeneral from '@/components/findInStore/FindInStoreSettingsGeneral.vue'
  import FindInStoreSettingsCatalog from '@/components/findInStore/FindInStoreSettingsCatalog.vue'
  import FindInStoreSettingsCustomization from '@/components/findInStore/FindInStoreSettingsCustomization.vue'
  import FindInStoreSettingsStock from '@/components/findInStore/FindInStoreSettingsStock.vue'
  import FindInStoreSettingsCheckout from '@/components/findInStore/FindInStoreSettingsCheckout.vue'

  import { ValidationObserver } from 'vee-validate'
  import { EventBus } from '@/event-bus'
  import { convert } from '../../helpers/helper'
  import countries from '@/data/countries.json'

  export default {
    name: 'FindInStoreSettings',
    components: {
      FindInStoreSettingsGeneral,
      FindInStoreSettingsCatalog,
      FindInStoreSettingsCustomization,
      FindInStoreSettingsStock,
      FindInStoreSettingsCheckout,
      ValidationObserver,
    },
    data() {
      return {
        countryCode: null,
        loading: false,
        settingsForm: {
          general: {
            mapProvider: 'google',
            geolocationMapApiKey: undefined,
            numberLocationToDisplay: 10,
            storeMinDistance: 1,
            storeMaxDistance: 100,
            storeMaxDistanceUpperLimit: 200,
            defaultSearchRadius: 100,
            defaultSearchUnit: 'km',
            locationScope: [],
            excludeLocationScope: false,
          },
          catalog: {
            enabled: false,
            apiKey: undefined,
            url: undefined,
            defaultStore: undefined,
          },
          customizations: {
            font: 'Nunito',
            primaryColor: undefined,
            backgroundColor: '#ffffff',
            borderRadius: 8,
          },
          stock: {
            updateInterval: undefined,
            identifierField: 'id',
            limitedQuantityThreshold: 3,
          },
          checkout: {
            enabled: false,
            url: '',
          },
        },
        areNewSettings: true,
        activeTab: 0,
        tabErrors: [false, false, false, false, false],
        loadingSettings: false, // different from loading to not load the form only on get settings
      }
    },
    mounted() {
      // set up as true here to avoid form to appear and disappear
      this.loadingSettings = true
      this.countryCode = this.$route.params?.id?.trim()
      if (this.isNotEmpty(this.countryCode) && !this.isAdd) {
        if (this.countryCodeExist) {
          return this.get()
        }
        return this.redirectToList(
          this.$t('findInStore.settings.invalidCountry', {
            countryCode: this.countryCode,
          })
        )
      } else {
        this.loadingSettings = false
      }
    },
    computed: {
      isAdd() {
        return this.$route.name === 'FindInStoreSettingsAdd'
      },
      countryCodeExist() {
        return (
          this.countryCode === 'default' || countries.some((country) => [country.iso_2_code].includes(this.countryCode))
        )
      },
    },
    methods: {
      async onSubmit() {
        const success = await this.$refs.form.validate()
        if (success) {
          if (this.areNewSettings && this.isAdd) {
            return this.create()
          }
          return this.update()
        }

        this.tabErrors = [false, false, false]

        const tabs = [
          { ref: 'generalTab', index: 0 },
          { ref: 'catalogTab', index: 1 },
          { ref: 'customizationTab', index: 2 },
          { ref: 'stockTab', index: 3 },
          { ref: 'checkoutTab', index: 4 },
        ]

        for (const tab of tabs) {
          const component = this.$refs[tab.ref]
          if (component?.hasErrors && (await component.hasErrors())) {
            this.$set(this.tabErrors, tab.index, true)
          }
        }

        const firstErrorTab = this.tabErrors.findIndex((e) => e)
        if (firstErrorTab !== -1) {
          this.activeTab = firstErrorTab
        }

        return EventBus.$emit('displaySnackbar', {
          display: true,
          text: this.$t('findInStore.settings.invalidSettings'),
          color: 'error',
        })
      },
      updateCatalogActivated(newValue) {
        this.settingsForm.catalog.enabled = newValue
      },
      updateCheckoutActivated(newValue) {
        this.settingsForm.checkout.enabled = newValue
      },
      create() {
        const reformatted = this.reformatSettings()
        if (this.loading) {
          return
        }
        this.loading = true
        const myInit = {
          headers: {
            Authorization: `Bearer ${this.$auth.accessToken}`,
          },
          body: reformatted,
        }
        this.$Amplify.API.post('findInStore', `/v1/int/settings`, myInit)
          .then(() => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('findInStore.settings.settingsCreated'),
              color: 'success',
            })
            this.loading = false
            this.areNewSettings = false
            this.$router.push({ path: `/find-in-store-settings/${reformatted.country_code}` })
          })
          .catch((e) => {
            this.loading = false
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('findInStore.settings.saveFailed'),
              color: 'error',
            })
          })
      },
      update() {
        const reformatted = this.reformatSettings()
        if (this.loading) {
          return
        }
        this.loading = true
        const myInit = {
          headers: {
            Authorization: `Bearer ${this.$auth.accessToken}`,
          },
          body: reformatted,
        }
        this.$Amplify.API.put('findInStore', `/v1/int/settings/${reformatted.country_code}`, myInit)
          .then(() => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('findInStore.settings.settingsUpdated'),
              color: 'success',
            })
            this.loading = false
          })
          .catch((e) => {
            this.loading = false
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('findInStore.settings.saveFailed'),
              color: 'error',
            })
          })
      },
      isNumber(val) {
        return !Number.isNaN(Number.parseInt(val, 10))
      },
      isNotEmpty(val) {
        return ![null, undefined, ''].includes(val?.trim())
      },
      setupSettingGeneral(setting) {
        if (this.isNotEmpty(setting?.frontend?.search?.default_search_unit)) {
          this.settingsForm.general.defaultSearchUnit = setting.frontend.search.default_search_unit
        }
        let searchUnit = this.settingsForm.general.defaultSearchUnit
        if (this.isNotEmpty(setting?.frontend?.map_settings?.provider)) {
          this.settingsForm.general.mapProvider = setting.frontend.map_settings.provider
        }
        if (this.isNotEmpty(setting?.frontend?.map_settings?.api_key)) {
          this.settingsForm.general.geolocationMapApiKey = setting.frontend.map_settings.api_key
        }
        if (this.isNumber(setting?.frontend?.store?.display_batch_size)) {
          this.settingsForm.general.numberLocationToDisplay = setting.frontend.store.display_batch_size
        }
        if (this.isNumber(setting?.frontend?.search?.min_search_radius)) {
          this.settingsForm.general.storeMinDistance = convert(
            setting.frontend.search.min_search_radius,
            'm',
            searchUnit
          )
        }
        if (this.isNumber(setting?.frontend?.search?.max_search_radius)) {
          this.settingsForm.general.storeMaxDistance = convert(
            setting.frontend.search.max_search_radius,
            'm',
            searchUnit
          )
        }
        if (this.isNumber(setting?.frontend?.search?.max_search_radius_admin)) {
          this.settingsForm.general.storeMaxDistanceUpperLimit = convert(
            setting.frontend.search.max_search_radius_admin,
            'm',
            searchUnit
          )
        }
        if (this.isNumber(setting?.frontend?.search?.default_search_radius)) {
          this.settingsForm.general.defaultSearchRadius = convert(
            setting.frontend.search.default_search_radius,
            'm',
            searchUnit
          )
        }
        if (Array.isArray(setting?.backend?.store?.scope) && setting.backend.store.scope.length) {
          this.settingsForm.general.locationScope = setting?.backend?.store?.scope
        }
        if (setting?.backend?.store?.exclude_scope) {
          this.settingsForm.general.excludeLocationScope = setting?.backend?.store?.exclude_scope
        }
      },
      setupSettingCatalog(setting) {
        if (![null, undefined, false].includes(setting?.backend?.catalog?.enabled)) {
          this.settingsForm.catalog.enabled = setting.backend.catalog.enabled
        }
        if (this.isNotEmpty(setting?.backend?.catalog?.api_key)) {
          this.settingsForm.catalog.apiKey = setting.backend.catalog.api_key
        }
        if (this.isNotEmpty(setting?.backend?.catalog?.url)) {
          this.settingsForm.catalog.url = setting.backend.catalog.url
        }
        if (this.isNotEmpty(setting?.backend?.catalog?.default_store)) {
          this.settingsForm.catalog.defaultStore = setting.backend.catalog.default_store
        }
      },
      setupSettingCustomization(setting) {
        if (this.isNotEmpty(setting?.frontend?.customizations?.font)) {
          this.settingsForm.customizations.font = setting.frontend.customizations.font
        }
        if (this.isNotEmpty(setting?.frontend?.customizations?.primary_color)) {
          this.settingsForm.customizations.primaryColor = setting.frontend.customizations.primary_color
        }
        if (this.isNotEmpty(setting?.frontend?.customizations?.background_color)) {
          this.settingsForm.customizations.backgroundColor = setting.frontend.customizations.background_color
        }
        if (this.isNumber(setting?.frontend?.customizations?.border_radius)) {
          this.settingsForm.customizations.borderRadius = setting.frontend.customizations.border_radius
        }
      },
      setupSettingStock(setting) {
        if (this.isNumber(setting?.frontend?.stock?.update_interval)) {
          this.settingsForm.stock.updateInterval = setting.frontend.stock.update_interval
        }
        if (this.isNotEmpty(setting?.frontend?.stock?.identifier_field)) {
          this.settingsForm.stock.identifierField = setting.frontend.stock.identifier_field
        }
        if (this.isNumber(setting?.backend?.stock_level?.max)) {
          this.settingsForm.stock.limitedQuantityThreshold = setting.backend.stock_level.max
        }
      },
      setupSettingCheckout(setting) {
        if (![null, undefined, false].includes(setting?.frontend?.checkout?.enabled)) {
          this.settingsForm.checkout.enabled = setting.frontend.checkout?.enabled
        }
        if (this.isNotEmpty(setting?.frontend?.checkout?.url)) {
          this.settingsForm.checkout.url = setting.frontend.checkout.url
        }
      },
      async get() {
        if (this.loading) {
          return
        }

        const myInit = {
          headers: {
            Authorization: `Bearer ${this.$auth.accessToken}`,
          },
        }

        try {
          const res = await this.$Amplify.API.get('findInStore', `/v1/int/settings/${this.countryCode}`, myInit)
          this.setupSettingGeneral(res)
          this.setupSettingCatalog(res)
          this.setupSettingCustomization(res)
          this.setupSettingStock(res)
          this.setupSettingCheckout(res)
          this.areNewSettings = false
        } catch (e) {
          console.log(e)
          const message = this.$t('settings.noCountry')

          if ([404, '404'].includes(e?.response?.status)) {
            this.areNewSettings = true
            return this.redirectToList(message)
          }

          EventBus.$emit('displaySnackbar', {
            display: true,
            text: message,
            color: 'error',
          })
        } finally {
          this.loadingSettings = false
        }
      },
      reformatSettings() {
        if ([null, undefined, ''].includes(this.$route.params?.id?.trim())) {
          return EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('findInStore.settings.missingCountryCode'),
            color: 'error',
          })
        }
        let searchUnit = this.settingsForm.general.defaultSearchUnit
        return {
          country_code: this.$route.params.id,
          backend: {
            catalog: {
              enabled: this.settingsForm.catalog.enabled,
              api_key: this.settingsForm.catalog.apiKey,
              url: this.settingsForm.catalog.url,
              default_store: this.settingsForm.catalog.defaultStore,
            },
            store: {
              scope: this.settingsForm.general.locationScope,
              exclude_scope: this.settingsForm.general.excludeLocationScope,
            },
            stock_level: {
              max: this.settingsForm.stock.limitedQuantityThreshold,
            },
          },
          frontend: {
            map_settings: {
              provider: this.settingsForm.general.mapProvider,
              api_key: this.settingsForm.general.geolocationMapApiKey,
            },
            customizations: {
              primary_color: this.settingsForm.customizations.primaryColor,
              background_color: this.settingsForm.customizations.backgroundColor,
              font: this.settingsForm.customizations.font,
              border_radius: this.settingsForm.customizations.borderRadius,
            },
            search: {
              min_search_radius: convert(this.settingsForm.general.storeMinDistance, searchUnit, 'm'),
              max_search_radius: convert(this.settingsForm.general.storeMaxDistance, searchUnit, 'm'),
              max_search_radius_admin: convert(this.settingsForm.general.storeMaxDistanceUpperLimit, searchUnit, 'm'),
              default_search_radius: convert(this.settingsForm.general.defaultSearchRadius, searchUnit, 'm'),
            },
            store: {
              display_batch_size: this.settingsForm.general.numberLocationToDisplay,
            },
            stock: {
              update_interval: this.settingsForm.stock.updateInterval,
              identifier_field: this.settingsForm.stock.identifierField,
            },
            checkout: {
              enabled: this.settingsForm.checkout.enabled,
              url: this.settingsForm.checkout.url,
            },
          },
        }
      },
      redirectToList(message = null) {
        let onComplete
        if (this.isNotEmpty(message)) {
          onComplete = () => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: message,
              color: 'error',
            })
          }
        }
        return this.$router.push(
          {
            name: 'FindInStoreSettingsTable',
          },
          onComplete
        )
      },
    },
  }
</script>

<style scoped>
  .error-tab {
    color: #f44336 !important;
  }
</style>
